import { useEffect, useRef, useState } from 'react';

import { CopyIcon, EllipsisVerticalIcon, LinkIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { NavLink, useMatch, useNavigate } from 'react-router';
import { toast } from 'sonner';

import type { ExportFormat } from '@/components/atoms/ExportButton/ExportButton';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { MODAL_DELETE } from '@/components/organisms/Modals/ModalConductor';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { useIsTruncated } from '@/hooks/useIsTruncated';
import { cn } from '@/lib/utils';
import { ExportSubmenu } from '@/modules/analytics/components/CustomReports/ExportSubmenu';
import { ReportExportJob } from '@/modules/analytics/components/CustomReports/ReportExportJob';
import { ReportNameForm } from '@/modules/analytics/components/CustomReports/ReportNameForm';
import { useReportPermissions } from '@/modules/analytics/hooks/useReportPermissions';
import {
  useDeleteReport,
  useDuplicateReport,
  useRenameReport,
  useReports,
} from '@/modules/analytics/hooks/useReports';
import { popModal, pushModal } from '@/redux/modals/actions';
import type { Report } from '@/shared/types/reports';
import { NAME_SUFFIX_BUDGET, truncateToServerLimit } from '@/util/checkNames';
import { scheduleScrollIntoScrollParent } from '@/util/dom';
import { LENGTH_XS } from '@/util/validation/constants';

import { navLinkClass } from './navLinkClass';

export const getCopyName = (name: string, existing: string[], copyLabel: string): string => {
  const base = truncateToServerLimit(
    name,
    LENGTH_XS - ` (${copyLabel})`.length - NAME_SUFFIX_BUDGET
  ).trimEnd();
  const taken = new Set(existing);
  let candidate = `${base} (${copyLabel})`;
  for (let n = 2; taken.has(candidate); n += 1) {
    candidate = `${base} (${copyLabel} ${n})`;
  }
  return candidate;
};

const EXPORT_TOAST_ID = 'report-export';

const NAME_TOOLTIP_OFFSET = 28;

const ReportNavLabel = ({ name }: { name: string }) => {
  const { ref, isTruncated } = useIsTruncated<HTMLSpanElement>('horizontal', [name]);

  return (
    <InfoTooltip
      content={isTruncated ? name : undefined}
      side="right"
      sideOffset={NAME_TOOLTIP_OFFSET}
      size="auto"
    >
      <span ref={ref} className="min-w-0 truncate">
        {name}
      </span>
    </InfoTooltip>
  );
};

type ReportOptionsProps = {
  report: Report;
  canWrite: boolean;
  canDelete: boolean;
  link: string;
  onExport: (format: ExportFormat) => void;
  onRename: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
};

const ReportOptions = ({
  report,
  canWrite,
  canDelete,
  link,
  onExport,
  onRename,
  onDuplicate,
  onDelete,
}: ReportOptionsProps) => {
  const { t } = useTranslation();

  const renameRequestedRef = useRef(false);

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${link}`);
      toast.success(t('common.copied_to_clipboard'));
    } catch {
      toast.error(t('common.copy_failed'));
    }
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          aria-label={t('analytics.reports_options', { 0: report.name })}
          className="nav-item-action absolute top-1/2 right-1 h-5 w-5 -translate-y-1/2 opacity-0 hover:bg-transparent focus:opacity-100 data-[state=open]:opacity-100"
        >
          <EllipsisVerticalIcon size={16} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-45"
        onCloseAutoFocus={(e) => {
          if (renameRequestedRef.current) {
            renameRequestedRef.current = false;
            e.preventDefault();
            onRename();
          }
        }}
      >
        <ExportSubmenu onExport={onExport} />
        <DropdownMenuItem onSelect={handleCopyLink}>
          <LinkIcon size={16} className="text-foreground-muted" />
          {t('common.copy_link')}
        </DropdownMenuItem>
        {canWrite && (
          <DropdownMenuItem onSelect={onDuplicate}>
            <CopyIcon size={16} className="text-foreground-muted" />
            {t('common.duplicate')}
          </DropdownMenuItem>
        )}
        {canWrite && (
          <DropdownMenuItem
            onSelect={() => {
              renameRequestedRef.current = true;
            }}
          >
            <PencilIcon size={16} className="text-foreground-muted" />
            {`${t('common.rename')}...`}
          </DropdownMenuItem>
        )}
        {canDelete && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={onDelete} variant="destructive">
              <Trash2Icon size={16} />
              {`${t('common.delete')}...`}
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

type Props = { slug: string };

export const ReportNavItems = ({ slug }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { reports, hasNextPage, fetchNextPage, isFetchingNextPage, isFetchNextPageError } =
    useReports();
  const { canWrite, canDelete } = useReportPermissions();
  const { mutate: deleteReport } = useDeleteReport();
  const { mutate: duplicateReport } = useDuplicateReport();
  const { mutate: renameReport, isPending: isRenaming } = useRenameReport();
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [exportJob, setExportJob] = useState<{
    id: number;
    reportId: string;
    format: ExportFormat;
  } | null>(null);
  const activeReportId = useMatch('/:slug/analytics/reports/:reportId')?.params.reportId;
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage && !isFetchNextPageError) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  useEffect(
    () => () => {
      toast.dismiss(EXPORT_TOAST_ID);
    },
    []
  );

  useEffect(() => {
    if (!activeReportId) {
      return undefined;
    }
    return scheduleScrollIntoScrollParent(
      () => listRef.current?.querySelector<HTMLElement>('[aria-current="page"]'),
      { block: 'nearest' }
    );
  }, [activeReportId, reports.length]);

  const reportPath = (reportId: string) => `/${slug}/analytics/reports/${reportId}`;

  const handleDuplicate = (report: Report) => {
    const name = getCopyName(
      report.name,
      reports.map((r) => r.name),
      t('analytics.reports_copy_suffix')
    );
    duplicateReport(
      { reportId: report.report_id, name },
      { onSuccess: (created) => navigate(reportPath(created.report_id)) }
    );
  };

  const handleExport = (reportId: string, format: ExportFormat) => {
    toast.loading(t('analytics.reports_exporting'), { id: EXPORT_TOAST_ID });
    setExportJob({ id: Date.now(), reportId, format });
  };

  const handleExportDone = () => {
    toast.dismiss(EXPORT_TOAST_ID);
    setExportJob(null);
  };

  const handleRename = (report: Report, name: string) => {
    if (name === report.name) {
      setRenamingId(null);
      return;
    }
    renameReport({ reportId: report.report_id, name }, { onSuccess: () => setRenamingId(null) });
  };

  const handleDelete = (report: Report) => {
    dispatch(
      pushModal(MODAL_DELETE, {
        title: t('analytics.reports_delete_confirm_title'),
        subtitle: (
          <Trans i18nKey="analytics.reports_delete_confirm_description" values={[report.name]} />
        ),
        onDelete: () => {
          dispatch(popModal());
          deleteReport(report.report_id, {
            onSuccess: () => {
              if (report.report_id === activeReportId) {
                navigate(`/${slug}/analytics/overview`, { replace: true });
              }
            },
          });
        },
      })
    );
  };

  return (
    <>
      <div ref={listRef} className="space-y-0.5">
        {reports.map((report) => (
          <Popover
            key={report.report_id}
            open={renamingId === report.report_id}
            onOpenChange={(open) => !open && setRenamingId(null)}
          >
            <PopoverAnchor asChild>
              <div className="group relative [&:focus-within_.nav-item-action]:opacity-100 [&:hover_.nav-item-action]:opacity-100">
                <NavLink
                  to={reportPath(report.report_id)}
                  className={(state) =>
                    cn(
                      navLinkClass(state),
                      'pr-7 group-hover:bg-highlight-background hover:bg-transparent'
                    )
                  }
                >
                  <ReportNavLabel name={report.name} />
                </NavLink>
                <ReportOptions
                  report={report}
                  canWrite={canWrite}
                  canDelete={canDelete}
                  link={reportPath(report.report_id)}
                  onExport={(format) => handleExport(report.report_id, format)}
                  onRename={() => setRenamingId(report.report_id)}
                  onDuplicate={() => handleDuplicate(report)}
                  onDelete={() => handleDelete(report)}
                />
              </div>
            </PopoverAnchor>
            <PopoverContent
              align="start"
              className="w-80 p-4"
              onFocusOutside={(e) => e.preventDefault()}
            >
              <ReportNameForm
                title={t('analytics.reports_rename_title')}
                defaultName={report.name}
                excludeName={report.name}
                submitLabel={t('common.save')}
                isSubmitting={isRenaming}
                onCancel={() => setRenamingId(null)}
                onSubmit={(name) => handleRename(report, name)}
              />
            </PopoverContent>
          </Popover>
        ))}
      </div>
      {exportJob && (
        <ReportExportJob
          key={exportJob.id}
          reportId={exportJob.reportId}
          format={exportJob.format}
          onDone={handleExportDone}
        />
      )}
    </>
  );
};
